/**
 * Signups-over-time bar chart. Ported from
 * aieweb/components/admin/signups-chart.tsx (recharts), restyled onto the
 * dashboard's Tailwind + parslee-brand tokens.
 *
 * Single combined series: the parent passes ONLY real-org rows as `orgs` and
 * orphan rows as `orphans` (demo & internal are excluded per the outcomes), so
 * the bars sum to Real + Orphan.
 *
 * Three-button bucket toggle (Day / Week / Month). Auto-picks on range change
 * based on calendar span; a user toggle is sticky UNTIL the range changes
 * again. `Month` is disabled at ≤14 days. Bucket changes are pure client-side
 * recompute — no network requests. "All time" maps to [earliest signup, now].
 */
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  autoPickBucket,
  bucketSignups,
  dateKeyInTimezone,
  deriveAllTimeRange,
  isBucketValid,
  resolveDateRange,
  type BucketSize,
  type DateRange,
  type ResolvedRange,
  type SignupDateRow,
} from "./signupsAggregation";

// Chart series on the brand palette (docs/brand/README.md); the keys are the series roles.
const CHART_COLORS = {
  emerald: "#c4894f", // deep gold
  sage: "#826545", // accent text
  charcoal: "#575859", // text secondary
} as const;

export interface SignupsChartProps {
  /** Real-org signup rows (demo excluded). */
  orgs: SignupDateRow[];
  /** Orphan-account signup rows. */
  orphans: SignupDateRow[];
  range: DateRange;
  /** Anchored to the projection's generatedAt (with staleness fallback). */
  now: Date;
  /** Resolved IANA timezone string. */
  timezone: string;
}

export function SignupsChart({
  orgs,
  orphans,
  range,
  now,
  timezone,
}: SignupsChartProps) {
  // Short-circuit an INVALID custom range to the empty state instead of
  // silently falling back to the all-time window.
  const invalidCustomRange =
    range.preset === "custom" &&
    !!range.customStart &&
    !!range.customEnd &&
    range.customStart > range.customEnd;

  const resolvedRange: ResolvedRange | null = useMemo(() => {
    if (invalidCustomRange) return null;
    return (
      resolveDateRange(range, now, timezone) ??
      deriveAllTimeRange([...orgs, ...orphans], now)
    );
  }, [range, now, timezone, orgs, orphans, invalidCustomRange]);

  // Auto-pick re-fires when the user's range INTENT changes — not when the
  // resolved [start, end) shifts each minute due to the parent ticker.
  const rangeIntentKey = useMemo(
    () => `${range.preset}_${range.customStart ?? ""}_${range.customEnd ?? ""}`,
    [range.preset, range.customStart, range.customEnd]
  );

  const [bucket, setBucket] = useState<BucketSize>(() =>
    resolvedRange ? autoPickBucket(resolvedRange.start, resolvedRange.end) : "day"
  );

  useEffect(() => {
    if (!resolvedRange) return;
    setBucket(autoPickBucket(resolvedRange.start, resolvedRange.end));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeIntentKey]);

  const buckets = useMemo(() => {
    if (!resolvedRange) return [];
    return bucketSignups(
      orgs,
      orphans,
      resolvedRange.start,
      resolvedRange.end,
      bucket,
      timezone
    );
  }, [orgs, orphans, resolvedRange, bucket, timezone]);

  const total = useMemo(
    () => buckets.reduce((sum, b) => sum + b.count, 0),
    [buckets]
  );
  const empty = total === 0;

  const monthValid = useMemo(
    () =>
      resolvedRange
        ? isBucketValid("month", resolvedRange.start, resolvedRange.end)
        : false,
    [resolvedRange]
  );

  const srDescription = useMemo(() => {
    if (!resolvedRange) return "Signups chart — no data window resolved.";
    const startStr = dateKeyInTimezone(resolvedRange.start, timezone);
    const endStr = dateKeyInTimezone(resolvedRange.end, timezone);
    return `Signups over time, ${bucket} buckets from ${startStr} to ${endStr}, total ${total}.`;
  }, [resolvedRange, bucket, total, timezone]);

  return (
    <div className="mb-6 border border-border rounded-md p-4 bg-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-medium text-foreground">
            Signups over time
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Real orgs + orphan accounts (excludes demo &amp; internal)
          </p>
        </div>
        <BucketToggle value={bucket} onChange={setBucket} monthEnabled={monthValid} />
      </div>

      <span className="sr-only" role="img" aria-label={srDescription}>
        {srDescription}
      </span>

      {empty ? (
        <div className="py-12 text-center text-muted-foreground">
          No signups in this window.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={buckets}>
            <CartesianGrid
              strokeDasharray="3 3"
              className="stroke-border"
            />
            <XAxis
              dataKey="label"
              stroke={CHART_COLORS.charcoal}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              allowDecimals={false}
              stroke={CHART_COLORS.charcoal}
              tick={{ fontSize: 11 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "white",
                border: `1px solid ${CHART_COLORS.sage}`,
                borderRadius: "0.375rem",
                fontSize: "0.875rem",
              }}
              formatter={(value) => [value as number, "Signups"]}
              cursor={{ fill: CHART_COLORS.sage, fillOpacity: 0.15 }}
            />
            <Bar dataKey="count" fill={CHART_COLORS.emerald} radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

interface BucketToggleProps {
  value: BucketSize;
  onChange: (next: BucketSize) => void;
  monthEnabled: boolean;
}

function BucketToggle({ value, onChange, monthEnabled }: BucketToggleProps) {
  const buttons: { key: BucketSize; label: string; enabled: boolean }[] = [
    { key: "day", label: "Day", enabled: true },
    { key: "week", label: "Week", enabled: true },
    { key: "month", label: "Month", enabled: monthEnabled },
  ];
  return (
    <div className="flex gap-1" role="group" aria-label="Chart bucket size">
      {buttons.map((b) => {
        const active = value === b.key;
        return (
          <button
            key={b.key}
            type="button"
            disabled={!b.enabled}
            aria-pressed={active}
            onClick={() => onChange(b.key)}
            className={`px-2.5 py-1 text-xs rounded border transition-colors ${
              active
                ? "bg-primary text-primary-foreground border-accent-text"
                : "bg-card text-text-secondary border-border hover:bg-muted"
            } ${!b.enabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {b.label}
          </button>
        );
      })}
    </div>
  );
}
